import { Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { BehaviorSubject } from 'rxjs';
import { LanguageService } from './language.service';

@Injectable({ providedIn: 'root' })
export class TranslationService {
  private translations: { [key: string]: any } = {
    'en': {},
    'ar': {}
  };
  private currentLangSubject = new BehaviorSubject<string>('en');
  public currentLang$ = this.currentLangSubject.asObservable();
  readonly currentLang = toSignal(this.currentLangSubject, { initialValue: 'en' });
  private loadedLangs: Set<string> = new Set();
  private translationsReadySubject = new BehaviorSubject<boolean>(false);
  public translationsReady$ = this.translationsReadySubject.asObservable();
  readonly translationsReady = toSignal(this.translationsReadySubject, { initialValue: false });

  constructor(private languageService: LanguageService) {
    Promise.all([
      this.loadTranslations('en'),
      this.loadTranslations('ar')
    ]).then(() => {
      const currentLang = this.languageService.current;
      this.setLanguage(currentLang);
      this.translationsReadySubject.next(true);
    });
  }

  private loadTranslations(lang: string): Promise<void> {
    if (this.loadedLangs.has(lang)) {
      return Promise.resolve();
    }
    
    return fetch(`/assets/i18n/${lang}.json`)
      .then(response => response.json())
      .then(data => {
        this.translations[lang] = data;
        this.loadedLangs.add(lang);
      })
      .catch(error => {
        console.error(`Failed to load translations for ${lang}:`, error);
        this.loadedLangs.add(lang);
      });
  }

  setLanguage(lang: string): void {
    if (!this.loadedLangs.has(lang)) {
      this.loadTranslations(lang).then(() => {
        this.currentLangSubject.next(lang);
      });
    } else {
      this.currentLangSubject.next(lang);
    }
  }

  get(key: string, defaultValue?: string): string {
    const lang = this.currentLangSubject.value;
    const keys = key.split('.');
    let value: any = this.translations[lang];

    for (const k of keys) {
      value = value?.[k];
    }

    return value || defaultValue || key;
  }

  instant(key: string, defaultValue?: string): string {
    return this.get(key, defaultValue);
  }
}
